/**
 * Goods Transfer Request View Modal Component
 * Modal popup version for viewing and approving GTRs
 */

import React, { useState, useEffect } from 'react';
import { useInventory } from '../../../contexts/InventoryContext';
import { useLocation } from '../../../contexts/LocationContext';
import adminApi from '../../../services/adminApi';
import LoadingSpinner from '../../Common/LoadingSpinner';
import ConfirmationModal from '../../Common/ConfirmationModal';
import GoodsIssueNoteForm from '../GoodsIssueNotes/GoodsIssueNoteForm';
import toast from 'react-hot-toast';

const GTRViewModal = ({ gtrId, onClose, onSuccess }) => {
  const {
    getGoodsTransferRequestById,
    submitGoodsTransferRequestForApproval,
    approveGoodsTransferRequest,
    rejectGoodsTransferRequest,
    cancelGoodsTransferRequest,
    getGTRStockMovements
  } = useInventory();
  const { currentLocation } = useLocation();

  const [gtr, setGtr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [stockMovements, setStockMovements] = useState(null);
  const [movementsLoading, setMovementsLoading] = useState(false);
  const [showIssueNoteForm, setShowIssueNoteForm] = useState(false);
  const [requestedByUser, setRequestedByUser] = useState(null);

  useEffect(() => {
    if (gtrId) {
      loadGTR();
    }
  }, [gtrId]);

  // requestedBy is a raw user id (not populated — inventory-service can't resolve
  // real user data locally, see requestedByName comment on the model). Resolve it
  // via the auth-service's no-elevated-permission-required lookup so older records
  // (created before requestedByName existed) still show a real name.
  useEffect(() => {
    const requestedById = typeof gtr?.requestedBy === 'string' ? gtr.requestedBy : null;
    if (!gtr || gtr.requestedByName || !requestedById) {
      return;
    }

    let cancelled = false;
    adminApi.getAssignableUsers({ ids: requestedById })
      .then(response => {
        if (cancelled) return;
        const user = response.data?.data?.users?.[0];
        if (user) setRequestedByUser(user);
      })
      .catch(err => console.error('Error resolving requestedBy user:', err));

    return () => { cancelled = true; };
  }, [gtr]);

  const getRequestedByDisplay = () => {
    if (gtr?.requestedByName) return gtr.requestedByName;
    if (requestedByUser) {
      const fullName = `${requestedByUser.firstName || ''} ${requestedByUser.lastName || ''}`.trim();
      return fullName || requestedByUser.email || 'Unknown';
    }
    return 'Unknown';
  };

  const loadGTR = async () => {
    try {
      setLoading(true);
      const response = await getGoodsTransferRequestById(gtrId);
      setGtr(response.data.data || response.data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load transfer request');
      toast.error('Failed to load transfer request details');
    } finally {
      setLoading(false);
    }
  };

  const loadStockMovements = async () => {
    try {
      setMovementsLoading(true);
      const response = await getGTRStockMovements(gtrId);
      setStockMovements(response.data || response);
    } catch (err) {
      console.error('Error loading stock movements:', err);
      setStockMovements(null);
    } finally {
      setMovementsLoading(false);
    }
  };

  const handleSubmitForApproval = () => {
    setConfirmAction({
      type: 'success',
      title: 'Approve Transfer Request',
      message: `Are you sure you want to approve "${gtr.requestNo}"? This does not move any stock yet — it will become visible to the source location, who will raise a Goods Issue Note to actually issue it.`,
      confirmText: 'Approve',
      onConfirm: async () => {
        try {
          await approveGoodsTransferRequest(gtrId, { comments: 'Approved via web interface' });

          toast.success('Transfer request approved — awaiting issuance at the source location');
          loadGTR();
          setConfirmAction(null);
          onClose();
          onSuccess && onSuccess();
        } catch (err) {
          toast.error('Failed to approve transfer request');
        }
      }
    });
  };

  const handleApprove = () => {
    setConfirmAction({
      type: 'success',
      title: 'Approve Transfer Request',
      message: `Are you sure you want to approve "${gtr.requestNo}"?`,
      confirmText: 'Approve',
      onConfirm: async () => {
        try {
          await approveGoodsTransferRequest(gtrId, { comments: 'Approved via web interface' });
          toast.success('Transfer request approved successfully');
          loadGTR();
          setConfirmAction(null);
          onClose();
          onSuccess && onSuccess();
        } catch (err) {
          toast.error('Failed to approve transfer request');
        }
      }
    });
  };

  const handleReject = () => {
    setConfirmAction({
      type: 'danger',
      title: 'Reject Transfer Request',
      message: `Are you sure you want to reject "${gtr.requestNo}"? Please provide a reason.`,
      confirmText: 'Reject',
      hasInput: true,
      inputLabel: 'Rejection Reason',
      inputRequired: true,
      onConfirm: async (reason) => {
        try {
          await rejectGoodsTransferRequest(gtrId, { reason, comments: 'Rejected via web interface' });
          toast.success('Transfer request rejected');
          loadGTR();
          onSuccess && onSuccess();
        } catch (err) {
          toast.error('Failed to reject transfer request');
        }
      }
    });
  };

  const handleCancel = () => {
    setConfirmAction({
      type: 'danger',
      title: 'Cancel Transfer Request',
      message: `Are you sure you want to cancel "${gtr.requestNo}"? This action cannot be undone.`,
      confirmText: 'Cancel Request',
      onConfirm: async () => {
        try {
          await cancelGoodsTransferRequest(gtrId, 'Cancelled via web interface');
          toast.success('Transfer request cancelled');
          loadGTR();
          onSuccess && onSuccess();
        } catch (err) {
          toast.error('Failed to cancel transfer request');
        }
      }
    });
  };

  // Status badge styling
  const getStatusBadgeColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'draft':
        return 'bg-white/20 text-white';
      case 'requested':
        return 'bg-yellow-500/20 text-yellow-100';
      case 'approved':
        return 'bg-green-500/20 text-green-100';
      case 'rejected':
        return 'bg-red-500/20 text-red-100';
      case 'cancelled':
        return 'bg-white/10 text-white/70';
      case 'converted_to_gi':
        return 'bg-indigo-500/20 text-indigo-100';
      case 'partially_fulfilled':
        return 'bg-blue-500/20 text-blue-100';
      case 'fulfilled':
        return 'bg-emerald-500/20 text-emerald-100';
      default:
        return 'bg-white/20 text-white';
    }
  };

  const getPriorityBadgeColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'urgent':
        return 'bg-red-500/20 text-red-100';
      default:
        return 'bg-white/20 text-white';
    }
  };

  const tabs = [
    { id: 'overview', name: 'Overview' },
    { id: 'items', name: 'Line Items', badge: gtr?.lineItems?.length },
    { id: 'movements', name: 'Stock Movements', badge: stockMovements?.movements?.length, disabled: !['approved', 'partially_fulfilled', 'fulfilled'].includes(gtr?.status) }
  ];

  const renderOverviewTab = () => (
    <div className="space-y-3">
      {/* Transfer Route */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-3 border border-blue-200">
        <h4 className="text-xs font-semibold text-gray-700 mb-2">Transfer Route</h4>
        <div className="flex items-center justify-between">
          <div className="text-center">
            <div className="text-sm font-medium text-gray-900">
              {gtr.sourceWarehouseName || 'Source'}
            </div>
            <div className="text-xs text-gray-600">Source Warehouse</div>
            {gtr.sourceLocationName && (
              <div className="text-xs text-gray-400">{gtr.sourceLocationName}</div>
            )}
          </div>
          <div className="flex items-center space-x-2">
            <div className="h-px bg-gray-300 flex-1 w-16"></div>
            <svg className="w-4 h-4 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
            <div className="h-px bg-gray-300 flex-1 w-16"></div>
          </div>
          <div className="text-center">
            <div className="text-sm font-medium text-gray-900">
              {gtr.destinationWarehouseName || 'Destination'}
            </div>
            <div className="text-xs text-gray-600">Destination Warehouse</div>
            {gtr.destinationLocationName && (
              <div className="text-xs text-gray-400">{gtr.destinationLocationName}</div>
            )}
          </div>
        </div>
      </div>

      {/* Request Details */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <div className="bg-gray-50 rounded-lg p-2.5 border border-gray-200">
          <div className="text-xs text-gray-500">Request Date</div>
          <div className="text-sm font-semibold text-gray-900">
            {new Date(gtr.requestDate).toLocaleDateString()}
          </div>
        </div>
        <div className="bg-gray-50 rounded-lg p-2.5 border border-gray-200">
          <div className="text-xs text-gray-500">Requested By</div>
          <div className="text-sm font-semibold text-gray-900 truncate">
            {getRequestedByDisplay()}
          </div>
        </div>
        <div className="bg-gray-50 rounded-lg p-2.5 border border-gray-200">
          <div className="text-xs text-gray-500">Total Items</div>
          <div className="text-sm font-semibold text-gray-900">
            {gtr.lineItems?.length || 0}
          </div>
        </div>
        <div className="bg-gray-50 rounded-lg p-2.5 border border-gray-200">
          <div className="text-xs text-gray-500">Total Amount</div>
          <div className="text-sm font-semibold text-green-600">
            {gtr.lineItems?.reduce((sum, item) => sum + (parseFloat(item.netAmount) || 0), 0).toFixed(2)}
          </div>
        </div>
      </div>

      {/* Transfer Progress Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <div className="bg-gray-50 rounded-lg p-2.5 border border-gray-200">
          <div className="text-xs text-gray-500">Requested Qty</div>
          <div className="text-sm font-semibold text-blue-600">
            {gtr.lineItems?.reduce((sum, item) => sum + (parseFloat(item.quantityRequested) || 0), 0)}
          </div>
        </div>
        <div className="bg-gray-50 rounded-lg p-2.5 border border-gray-200">
          <div className="text-xs text-gray-500">Approved Qty</div>
          <div className="text-sm font-semibold text-orange-600">
            {gtr.lineItems?.reduce((sum, item) => sum + (parseFloat(item.quantityApproved) || 0), 0)}
          </div>
        </div>
        <div className="bg-gray-50 rounded-lg p-2.5 border border-gray-200">
          <div className="text-xs text-gray-500">Transferred Qty</div>
          <div className="text-sm font-semibold text-green-600">
            {gtr.lineItems?.reduce((sum, item) => sum + (parseFloat(item.quantityTransferred) || 0), 0)}
          </div>
        </div>
        <div className="bg-gray-50 rounded-lg p-2.5 border border-gray-200">
          <div className="text-xs text-gray-500">Progress</div>
          <div className="text-sm font-semibold text-purple-600">
            {(() => {
              const totalRequested = gtr.lineItems?.reduce((sum, item) => sum + (parseFloat(item.quantityRequested) || 0), 0) || 1;
              const totalTransferred = gtr.lineItems?.reduce((sum, item) => sum + (parseFloat(item.quantityTransferred) || 0), 0) || 0;
              const percentage = Math.round((totalTransferred / totalRequested) * 100);
              return `${percentage}%`;
            })()}
          </div>
        </div>
      </div>

      {/* Additional Details */}
      {gtr.transferReference && (
        <div className="bg-gray-50 rounded-lg p-2.5 border border-gray-200">
          <h4 className="text-xs font-semibold text-gray-700 mb-1">Transfer Reference</h4>
          <p className="text-xs text-gray-600">{gtr.transferReference}</p>
        </div>
      )}
    </div>
  );

  const renderItemsTab = () => (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <div className="bg-gray-50 px-3 py-1.5 border-b border-gray-200">
        <span className="text-xs font-semibold text-gray-700">Line Items</span>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-max w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-2 py-1.5 text-left text-xs font-semibold text-gray-600">Item</th>
              <th className="px-2 py-1.5 text-center text-xs font-semibold text-gray-600">Requested</th>
              <th className="px-2 py-1.5 text-center text-xs font-semibold text-gray-600">Approved</th>
              <th className="px-2 py-1.5 text-center text-xs font-semibold text-gray-600">Transferred</th>
              <th className="px-2 py-1.5 text-center text-xs font-semibold text-gray-600">UOM</th>
              <th className="px-2 py-1.5 text-right text-xs font-semibold text-gray-600">Unit Price</th>
              <th className="px-2 py-1.5 text-right text-xs font-semibold text-gray-600">Net Amount</th>
              <th className="px-2 py-1.5 text-center text-xs font-semibold text-gray-600">Status</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-100">
            {gtr.lineItems?.map((item, index) => (
              <tr key={index} className="hover:bg-gray-50">
                <td className="px-2 py-1.5">
                  <div className="text-xs font-medium text-gray-900">
                    {item.itemCode || item.item?.sku || item.assetCode}
                  </div>
                  <div className="text-xs text-gray-500">
                    {item.itemDescription || item.item?.name || item.assetName}
                  </div>
                </td>
                <td className="px-2 py-1.5 text-center text-xs text-gray-700">
                  {item.quantityRequested}
                </td>
                <td className="px-2 py-1.5 text-center text-xs text-gray-700">
                  {item.quantityApproved || 0}
                </td>
                <td className="px-2 py-1.5 text-center text-xs">
                  <span className={`font-medium ${(item.quantityTransferred || 0) > 0 ? 'text-green-600' : 'text-gray-400'}`}>
                    {item.quantityTransferred || 0}
                  </span>
                  {item.transferredAt && (
                    <div className="text-[10px] text-gray-500">
                      {new Date(item.transferredAt).toLocaleDateString()}
                    </div>
                  )}
                </td>
                <td className="px-2 py-1.5 text-center text-xs text-gray-700">
                  {item.uom}
                </td>
                <td className="px-2 py-1.5 text-right text-xs text-gray-700">
                  {(item.unitPrice || 0).toFixed(2)}
                </td>
                <td className="px-2 py-1.5 text-right text-xs font-medium text-gray-900">
                  {(item.netAmount || 0).toFixed(2)}
                </td>
                <td className="px-2 py-1.5 text-center">
                  <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium border ${item.status === 'transferred'
                    ? 'bg-green-100 text-green-700 border-green-300'
                    : item.status === 'partially_transferred'
                      ? 'bg-blue-100 text-blue-700 border-blue-300'
                      : item.status === 'approved'
                        ? 'bg-orange-100 text-orange-700 border-orange-300'
                        : item.status === 'rejected'
                          ? 'bg-red-100 text-red-700 border-red-300'
                          : 'bg-gray-100 text-gray-700 border-gray-300'
                    }`}>
                    {item.status || 'pending'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderMovementsTab = () => {
    if (!['approved', 'partially_fulfilled', 'fulfilled'].includes(gtr?.status)) {
      return (
        <div className="px-3 py-8 text-center text-xs text-gray-400">
          Stock movements will be available once a Goods Issue Note is approved for this transfer.
        </div>
      );
    }

    // Load movements if not loaded yet
    if (!stockMovements && !movementsLoading) {
      loadStockMovements();
    }

    if (movementsLoading) {
      return (
        <div className="flex items-center justify-center py-8">
          <LoadingSpinner />
        </div>
      );
    }

    if (!stockMovements || !stockMovements.movements?.length) {
      return (
        <div className="px-3 py-8 text-center text-xs text-gray-400">
          No stock movements have been recorded for this transfer yet.
        </div>
      );
    }

    return (
      <div className="space-y-3">
        {/* Movements Summary */}
        <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-lg p-3 border border-green-200">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-center">
            <div>
              <div className="text-lg font-bold text-red-600">
                {stockMovements.summary.totals.outQuantity}
              </div>
              <div className="text-xs text-gray-600">Total Out</div>
            </div>
            <div>
              <div className="text-lg font-bold text-green-600">
                {stockMovements.summary.totals.inQuantity}
              </div>
              <div className="text-xs text-gray-600">Total In</div>
            </div>
            <div>
              <div className="text-lg font-bold text-blue-600">
                {stockMovements.summary.movements.total}
              </div>
              <div className="text-xs text-gray-600">Total Movements</div>
            </div>
            <div>
              <div className="text-lg font-bold text-purple-600">
                {stockMovements.summary.totals.totalValue.toFixed(2)}
              </div>
              <div className="text-xs text-gray-600">Total Value</div>
            </div>
          </div>
        </div>

        {/* Stock Movements Table */}
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <div className="bg-gray-50 px-3 py-1.5 border-b border-gray-200">
            <span className="text-xs font-semibold text-gray-700">Movement Details</span>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-max w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-2 py-1.5 text-left text-xs font-semibold text-gray-600">Movement #</th>
                  <th className="px-2 py-1.5 text-left text-xs font-semibold text-gray-600">Type</th>
                  <th className="px-2 py-1.5 text-left text-xs font-semibold text-gray-600">Item</th>
                  <th className="px-2 py-1.5 text-left text-xs font-semibold text-gray-600">Warehouse</th>
                  <th className="px-2 py-1.5 text-center text-xs font-semibold text-gray-600">Direction</th>
                  <th className="px-2 py-1.5 text-right text-xs font-semibold text-gray-600">Quantity</th>
                  <th className="px-2 py-1.5 text-right text-xs font-semibold text-gray-600">Before</th>
                  <th className="px-2 py-1.5 text-right text-xs font-semibold text-gray-600">After</th>
                  <th className="px-2 py-1.5 text-left text-xs font-semibold text-gray-600">Date</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {stockMovements.movements.map((movement) => (
                  <tr key={movement._id} className="hover:bg-gray-50">
                    <td className="px-2 py-1.5 text-xs font-medium text-gray-900">
                      {movement.movementNumber}
                    </td>
                    <td className="px-2 py-1.5">
                      <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium border ${movement.direction === 'out'
                        ? 'bg-red-100 text-red-700 border-red-300'
                        : 'bg-green-100 text-green-700 border-green-300'
                        }`}>
                        {movement.movementType.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-2 py-1.5 text-xs text-gray-900">
                      <div className="font-medium">{movement.item?.sku}</div>
                      <div className="text-gray-500">{movement.item?.name}</div>
                    </td>
                    <td className="px-2 py-1.5 text-xs text-gray-900">
                      <div className="font-medium">{movement.warehouse?.name}</div>
                      <div className="text-gray-500">{movement.warehouse?.code}</div>
                    </td>
                    <td className="px-2 py-1.5 text-center">
                      <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${movement.direction === 'out'
                        ? 'bg-red-100 text-red-700'
                        : 'bg-green-100 text-green-700'
                        }`}>
                        {movement.direction.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-2 py-1.5 text-right text-xs">
                      <span className={`font-medium ${movement.direction === 'out' ? 'text-red-600' : 'text-green-600'}`}>
                        {movement.direction === 'out' ? '-' : '+'}{movement.quantity} {movement.unit}
                      </span>
                    </td>
                    <td className="px-2 py-1.5 text-right text-xs text-gray-600">
                      {movement.beforeQuantity}
                    </td>
                    <td className="px-2 py-1.5 text-right text-xs text-gray-600">
                      {movement.afterQuantity}
                    </td>
                    <td className="px-2 py-1.5 text-xs text-gray-500">
                      {new Date(movement.transactionDate).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/20 backdrop-blur-sm overflow-y-auto h-full w-full z-50 flex items-center justify-center">
        <div className="bg-white rounded-lg p-8">
          <LoadingSpinner />
          <p className="mt-4 text-center text-sm text-gray-600">Loading transfer request...</p>
        </div>
      </div>
    );
  }

  if (error || !gtr) {
    return (
      <div className="fixed inset-0 bg-black/20 backdrop-blur-sm overflow-y-auto h-full w-full z-50 flex items-center justify-center">
        <div className="bg-white rounded-lg p-6 max-w-md">
          <div className="text-center">
            <svg className="mx-auto h-10 w-10 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">Error Loading Request</h3>
            <p className="mt-1 text-xs text-gray-500">{error}</p>
            <button
              onClick={onClose}
              className="mt-4 inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/20 backdrop-blur-sm overflow-y-auto h-full w-full z-50">
      <div className="flex items-center justify-center min-h-full px-4 py-6">
        <div className="relative w-full max-w-5xl bg-white rounded-lg shadow-xl">

          {/* Header */}
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-t-lg px-4 py-2.5">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <div className="w-6 h-6 bg-white/20 rounded flex items-center justify-center">
                  <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                  </svg>
                </div>
                <h3 className="text-sm font-semibold text-white">{gtr.requestNo}</h3>
                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getStatusBadgeColor(gtr.status)}`}>
                  {gtr.status?.replace('_', ' ')}
                </span>
                {gtr.priority === 'urgent' && (
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getPriorityBadgeColor(gtr.priority)}`}>
                    {gtr.priority}
                  </span>
                )}
              </div>
              <button
                onClick={onClose}
                className="w-8 h-8 rounded-lg flex items-center justify-center text-white/80 hover:text-white hover:bg-white/20 transition-all"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Action Buttons */}
            <div className="mt-2 flex flex-wrap justify-end gap-1.5">
              {gtr.status === 'draft' && (
                <button
                  onClick={handleSubmitForApproval}
                  className="inline-flex items-center px-2.5 py-1 border border-transparent rounded text-xs font-medium text-white bg-green-500 hover:bg-green-400"
                >
                  Approve
                </button>
              )}

              {gtr.status === 'approved' && currentLocation && (currentLocation._id || currentLocation.id) === (gtr.sourceLocation?._id || gtr.sourceLocation) && (
                <button
                  onClick={() => setShowIssueNoteForm(true)}
                  className="inline-flex items-center px-2.5 py-1 border border-transparent rounded text-xs font-medium text-white bg-indigo-500 hover:bg-indigo-400"
                >
                  Convert to Goods Issue Note
                </button>
              )}

              {gtr.status === 'converted_to_gi' && currentLocation && (currentLocation._id || currentLocation.id) === (gtr.sourceLocation?._id || gtr.sourceLocation) && (
                <span className="inline-flex items-center px-2.5 py-1 rounded text-xs font-medium text-white/80 bg-white/10">
                  Goods Issue Note already raised — awaiting dispatch
                </span>
              )}

              {gtr.status === 'pending' && (
                <>
                  <button
                    onClick={handleApprove}
                    className="inline-flex items-center px-2.5 py-1 border border-transparent rounded text-xs font-medium text-white bg-green-500 hover:bg-green-400"
                  >
                    Approve
                  </button>
                  <button
                    onClick={handleReject}
                    className="inline-flex items-center px-2.5 py-1 border border-white/20 rounded text-xs font-medium text-white bg-red-500 hover:bg-red-400"
                  >
                    Reject
                  </button>
                </>
              )}

              {['draft', 'requested'].includes(gtr.status) && (
                <button
                  onClick={handleCancel}
                  className="inline-flex items-center px-2.5 py-1 border border-white/20 rounded text-xs font-medium text-white bg-white/10 hover:bg-white/20"
                >
                  Cancel Request
                </button>
              )}
            </div>
          </div>

          {/* Tabs */}
          <div className="flex items-center gap-1 border-b border-gray-200 px-4 bg-white">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => !tab.disabled && setActiveTab(tab.id)}
                disabled={tab.disabled}
                className={`px-3 py-2 text-xs font-medium border-b-2 transition-colors flex items-center gap-1.5 ${tab.disabled
                  ? 'border-transparent text-gray-300 cursor-not-allowed'
                  : activeTab === tab.id
                    ? 'border-indigo-600 text-indigo-700'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
              >
                {tab.name}
                {tab.badge !== undefined && !tab.disabled && (
                  <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium ${activeTab === tab.id ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-600'
                    }`}>
                    {tab.badge}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="px-4 py-3 max-h-[55vh] overflow-y-auto">
            {activeTab === 'overview' && renderOverviewTab()}
            {activeTab === 'items' && renderItemsTab()}
            {activeTab === 'movements' && renderMovementsTab()}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-2 px-4 py-2.5 border-t border-gray-100">
            <button
              onClick={onClose}
              className="px-3 py-1.5 text-xs text-gray-600 bg-gray-100 hover:bg-gray-200 rounded transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      {confirmAction && (
        <ConfirmationModal
          isOpen={!!confirmAction}
          onClose={() => setConfirmAction(null)}
          onConfirm={confirmAction.onConfirm}
          title={confirmAction.title}
          message={confirmAction.message}
          confirmText={confirmAction.confirmText}
          type={confirmAction.type}
          hasInput={confirmAction.hasInput}
          inputLabel={confirmAction.inputLabel}
          inputRequired={confirmAction.inputRequired}
        />
      )}

      {/* Goods Issue Note Form */}
      {showIssueNoteForm && (
        <GoodsIssueNoteForm
          gtr={gtr}
          onClose={() => setShowIssueNoteForm(false)}
          onSuccess={() => {
            // GIN created successfully — the GTR is now 'converted_to_gi', nothing left to
            // do here, so close this view too instead of leaving it open on a stale state.
            onSuccess && onSuccess();
            onClose();
          }}
        />
      )}
    </div>
  );
};

export default GTRViewModal;
